/**
 * KioskSecurityWrapper - Browser-side kiosk security features
 * 
 * Features:
 * 1. Auto-fullscreen on load
 * 2. Secret admin exit (long-press logo + PIN)
 * 3. Keyboard/gesture blocking
 * 4. Fullscreen exit detection with re-prompt
 * 
 * ADMIN EXIT INSTRUCTIONS:
 * - Long-press (5 seconds) on the Bulaccino logo in the top-left corner
 * - Enter PIN: 147258
 * - Click "Exit Kiosk Mode" to leave fullscreen
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { ShieldAlert, Lock, Unlock, X, AlertTriangle } from 'lucide-react';

// Security PIN - change this for your deployment
const ADMIN_PIN = '147258';
const LONG_PRESS_DURATION = 5000; // 5 seconds

export default function KioskSecurityWrapper({ children }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [showPinEntry, setShowPinEntry] = useState(false);
  const [enteredPin, setEnteredPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [exitUnlocked, setExitUnlocked] = useState(false);
  const [longPressProgress, setLongPressProgress] = useState(0);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false);
  
  const longPressTimer = useRef(null);
  const progressInterval = useRef(null);

  // Enter fullscreen
  const enterFullscreen = useCallback(async () => {
    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
      setShowFullscreenPrompt(false);
    } catch (err) {
      console.log('Fullscreen request failed:', err);
      // Show prompt to manually enter fullscreen
      setShowFullscreenPrompt(true);
    }
  }, []);

  // Exit fullscreen
  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      setIsFullscreen(false);
      setExitUnlocked(false);
      setShowExitPrompt(false);
    } catch (err) {
      console.log('Exit fullscreen failed:', err);
    }
  }, []);

  // Check fullscreen state
  const checkFullscreen = useCallback(() => {
    const isFS = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    );
    setIsFullscreen(isFS);
    
    // If not fullscreen and exit not unlocked, show prompt
    if (!isFS && !exitUnlocked) {
      setShowFullscreenPrompt(true);
    }
  }, [exitUnlocked]);

  // Auto-enter fullscreen on mount
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      enterFullscreen();
    }, 500);

    return () => clearTimeout(timer);
  }, [enterFullscreen]);

  // Listen for fullscreen changes
  useEffect(() => {
    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('msfullscreenchange', checkFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('msfullscreenchange', checkFullscreen);
    };
  }, [checkFullscreen]);

  // Block keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Allow PIN entry when modal is open
      if (showPinEntry) {
        if (e.key === 'Escape') {
          setShowPinEntry(false);
          setEnteredPin('');
          setPinError(false);
          e.preventDefault();
        }
        return;
      }

      // Block dangerous shortcuts
      const blockedKeys = [
        'F11', // Fullscreen toggle
        'F12', // Dev tools
        'F5',  // Refresh (optional)
      ];

      const blockedCombos = [
        { ctrl: true, key: 'w' },   // Close tab
        { ctrl: true, key: 't' },   // New tab
        { ctrl: true, key: 'n' },   // New window
        { ctrl: true, key: 'l' },   // Focus address bar
        { ctrl: true, key: 'd' },   // Bookmark
        { ctrl: true, shift: true, key: 'i' }, // Dev tools
        { ctrl: true, shift: true, key: 'j' }, // Console
        { alt: true, key: 'F4' },   // Close window
        { alt: true, key: 'Tab' },  // Switch app (limited effect)
      ];

      // Check if key is blocked
      if (blockedKeys.includes(e.key)) {
        e.preventDefault();
        return;
      }

      // Check combos
      for (const combo of blockedCombos) {
        if (
          (combo.ctrl === undefined || combo.ctrl === e.ctrlKey) &&
          (combo.shift === undefined || combo.shift === e.shiftKey) &&
          (combo.alt === undefined || combo.alt === e.altKey) &&
          combo.key.toLowerCase() === e.key.toLowerCase()
        ) {
          e.preventDefault();
          return;
        }
      }

      // Block Escape when in fullscreen (unless exit is unlocked)
      if (e.key === 'Escape' && isFullscreen && !exitUnlocked) {
        e.preventDefault();
        // Show a subtle indicator that escape is blocked
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isFullscreen, exitUnlocked, showPinEntry]);

  // Block right-click context menu
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (!exitUnlocked) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, [exitUnlocked]);

  // Long press handlers for secret exit
  const handleLogoTouchStart = useCallback(() => {
    setLongPressProgress(0);
    
    // Start progress animation
    let progress = 0;
    progressInterval.current = setInterval(() => {
      progress += 2;
      setLongPressProgress(Math.min(progress, 100));
    }, LONG_PRESS_DURATION / 50);

    // Start timer for long press completion
    longPressTimer.current = setTimeout(() => {
      clearInterval(progressInterval.current);
      setLongPressProgress(0);
      setShowPinEntry(true);
    }, LONG_PRESS_DURATION);
  }, []);

  const handleLogoTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    setLongPressProgress(0);
  }, []);

  // PIN verification
  const handlePinSubmit = useCallback(() => {
    if (enteredPin === ADMIN_PIN) {
      setExitUnlocked(true);
      setShowPinEntry(false);
      setShowExitPrompt(true);
      setEnteredPin('');
      setPinError(false);
    } else {
      setPinError(true);
      setEnteredPin('');
      // Reset error after 2 seconds
      setTimeout(() => setPinError(false), 2000);
    }
  }, [enteredPin]);

  // PIN keypad handler
  const handlePinKey = useCallback((key) => {
    if (key === 'clear') {
      setEnteredPin('');
      setPinError(false);
    } else if (key === 'back') {
      setEnteredPin(prev => prev.slice(0, -1));
    } else if (enteredPin.length < 6) {
      const newPin = enteredPin + key;
      setEnteredPin(newPin);
      
      // Auto-submit when 6 digits entered
      if (newPin.length === 6) {
        setTimeout(() => {
          if (newPin === ADMIN_PIN) {
            setExitUnlocked(true);
            setShowPinEntry(false);
            setShowExitPrompt(true);
            setEnteredPin('');
            setPinError(false);
          } else {
            setPinError(true);
            setEnteredPin('');
            setTimeout(() => setPinError(false), 2000);
          }
        }, 200);
      }
    }
  }, [enteredPin]);

  return (
    <>
      {/* Main kiosk container - 90deg rotation for portrait mode on landscape monitor */}
      <div 
        className="kiosk-container fixed inset-0 bg-black"
        style={{
          transform: 'rotate(90deg)',
          transformOrigin: 'center center',
          width: '100vh',
          height: '100vw',
          left: '50%',
          top: '50%',
          marginLeft: '-50vh',
          marginTop: '-50vw',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div className="relative w-full min-h-full">
          {/* Secret Admin Exit Trigger - BOTTOM-left corner (relative to rotated view) */}
          {/* Press and hold for 5 seconds to unlock admin exit */}
          <div
            className="fixed w-24 h-24 z-[9999] cursor-default"
            style={{
              bottom: '0',
              left: '0'
            }}
            onTouchStart={handleLogoTouchStart}
            onTouchEnd={handleLogoTouchEnd}
            onMouseDown={handleLogoTouchStart}
            onMouseUp={handleLogoTouchEnd}
            onMouseLeave={handleLogoTouchEnd}
            data-testid="kiosk-admin-trigger"
          >
            {/* Long press progress indicator (subtle) */}
            {longPressProgress > 0 && (
              <div className="absolute inset-2 rounded-full border-4 border-transparent" 
                style={{
                  background: `conic-gradient(rgba(34, 84, 61, 0.3) ${longPressProgress}%, transparent ${longPressProgress}%)`
                }}
              />
            )}
          </div>

          {/* Main Content */}
          {children}

          {/* Fullscreen Prompt Overlay */}
          {showFullscreenPrompt && !exitUnlocked && (
            <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[10000]">
              <div className="bg-white rounded-3xl p-8 max-w-md mx-4 text-center shadow-2xl">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShieldAlert className="w-10 h-10 text-green-700" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Enter Kiosk Mode
                </h2>
                <p className="text-gray-600 mb-6">
                  Tap the button below to enter fullscreen kiosk mode for the best ordering experience.
                </p>
                <button
                  onClick={enterFullscreen}
                  className="w-full py-4 bg-green-700 hover:bg-green-800 text-white text-xl font-semibold rounded-2xl transition-colors"
                  data-testid="enter-fullscreen-btn"
                >
                  Enter Fullscreen
                </button>
              </div>
            </div>
          )}

      {/* PIN Entry Modal */}
      {showPinEntry && (
        <div className="absolute inset-0 bg-black/90 flex items-center justify-center z-[10001]">
          <div className="bg-gray-900 rounded-3xl p-6 max-w-sm mx-4 shadow-2xl border border-gray-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-amber-500" />
                <h2 className="text-xl font-bold text-white">Admin Access</h2>
              </div>
              <button
                onClick={() => {
                  setShowPinEntry(false);
                  setEnteredPin('');
                  setPinError(false);
                }}
                className="p-2 hover:bg-gray-800 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* PIN Display */}
            <div className={`flex justify-center gap-3 mb-6 ${pinError ? 'animate-shake' : ''}`}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-2xl font-mono ${
                    pinError 
                      ? 'border-red-500 bg-red-500/20' 
                      : enteredPin[i] 
                        ? 'border-green-500 bg-green-500/20' 
                        : 'border-gray-600 bg-gray-800'
                  }`}
                >
                  {enteredPin[i] ? '•' : ''}
                </div>
              ))}
            </div>

            {/* Error Message */}
            {pinError && (
              <div className="flex items-center justify-center gap-2 mb-4 text-red-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">Incorrect PIN</span>
              </div>
            )}

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'clear', 0, 'back'].map((key) => (
                <button
                  key={key}
                  onClick={() => handlePinKey(key.toString())}
                  className={`h-14 rounded-xl font-semibold text-lg transition-all active:scale-95 ${
                    key === 'clear' 
                      ? 'bg-red-600 hover:bg-red-700 text-white text-sm' 
                      : key === 'back'
                        ? 'bg-gray-700 hover:bg-gray-600 text-white text-sm'
                        : 'bg-gray-800 hover:bg-gray-700 text-white'
                  }`}
                >
                  {key === 'clear' ? 'Clear' : key === 'back' ? '←' : key}
                </button>
              ))}
            </div>

            <p className="text-center text-gray-500 text-xs mt-4">
              Enter 6-digit admin PIN
            </p>
          </div>
        </div>
      )}

      {/* Exit Confirmation Modal (after PIN verified) */}
      {showExitPrompt && exitUnlocked && (
        <div className="absolute inset-0 bg-black/90 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-3xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Unlock className="w-8 h-8 text-green-600" />
              <h2 className="text-2xl font-bold text-gray-900">Admin Mode Unlocked</h2>
            </div>
            
            <p className="text-gray-600 text-center mb-6">
              You now have admin access. Choose an action:
            </p>

            <div className="space-y-3">
              <button
                onClick={exitFullscreen}
                className="w-full py-4 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold rounded-2xl transition-colors"
                data-testid="exit-kiosk-btn"
              >
                Exit Kiosk Mode
              </button>
              
              <button
                onClick={() => window.location.reload()}
                className="w-full py-4 bg-amber-600 hover:bg-amber-700 text-white text-lg font-semibold rounded-2xl transition-colors"
              >
                Restart Kiosk
              </button>
              
              <button
                onClick={() => {
                  setShowExitPrompt(false);
                  setExitUnlocked(false);
                  if (!isFullscreen) {
                    enterFullscreen();
                  }
                }}
                className="w-full py-4 bg-gray-200 hover:bg-gray-300 text-gray-800 text-lg font-semibold rounded-2xl transition-colors"
              >
                Cancel & Return to Kiosk
              </button>
            </div>

            <p className="text-center text-gray-400 text-xs mt-6">
              Session will auto-lock after 30 seconds of inactivity
            </p>
          </div>
        </div>
      )}

      {/* CSS for shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
        </div>
      </div>
    </>
  );
}
